import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';

import {
    Cliente,
    ErrorApi,
    crearT,
    detectar,
    type Capacidades,
    type DiaDeTrabajo,
    type Idioma,
    type T,
} from '@haus/core';

import {
    AJUSTES_POR_DEFECTO,
    borrarSesion,
    guardarAjustes,
    guardarDia,
    guardarSesion,
    leerAjustes,
    leerSesion,
    pedirPersistencia,
    ultimoDia,
    type Ajustes,
    type SesionGuardada,
} from '../datos/db.js';
import { Cola, type ResumenCola } from '../datos/cola.js';
import { tokenDeInvitacion, tokenQrDeUrl, limpiarUrl, type Pantalla, type Sentido } from './navegacion.js';

/**
 * Cómo entra una pantalla en la pila:
 *
 *   'apila' ..... encima de la actual. Lo normal.
 *   'reemplaza' . en lugar de la actual, que desaparece de la pila. Para los
 *                 pasos que no se deben volver a visitar con el botón atrás:
 *                 el escáner de QR una vez ha abierto el apartamento.
 *   'raiz' ...... vacía la pila. Al finalizar una limpieza se vuelve al día y
 *                 no tiene sentido poder retroceder a un resumen ya cerrado.
 */
export type Modo = 'apila' | 'reemplaza' | 'raiz';

interface Contexto {
    pantalla: Pantalla;
    sentido: Sentido;
    /** Cambia con cada navegación: las pantallas lo usan para releer de IndexedDB. */
    sello: number;
    ir: (p: Pantalla, modo?: Modo) => void;
    atras: () => void;

    sesion: SesionGuardada | null;
    guardar: (s: SesionGuardada) => Promise<void>;
    salir: () => Promise<void>;

    ajustes: Ajustes;
    cambiarAjustes: (parcial: Partial<Ajustes>) => Promise<void>;

    idioma: Idioma;
    t: T;

    cliente: Cliente;
    cola: Cola;
    resumenCola: ResumenCola | null;
    capacidades: Capacidades;

    dia: DiaDeTrabajo | null;
    hayRed: boolean;
    cargandoDia: boolean;
    recargarDia: () => Promise<void>;
}

const ctx = createContext<Contexto | null>(null);

export function useApp(): Contexto {
    const c = useContext(ctx);
    if (c === null) {
        throw new Error('useApp fuera del proveedor');
    }

    return c;
}

export function ProveedorApp({ children }: { children: ReactNode }) {
    const [pantalla, setPantalla] = useState<Pantalla>({ n: 'cargando' });
    const [sentido, setSentido] = useState<Sentido>('adelante');
    // Un contador que cambia con cada navegación. Las pantallas que leen de
    // IndexedDB dependen de él para releer al volver: React no remonta un
    // componente por navegar a la misma pantalla, así que sin esto el
    // recorrido enseñaría el estado de antes de grabar.
    const [sello, setSello] = useState(0);
    const [sesion, setSesion] = useState<SesionGuardada | null>(null);
    // El estado inicial sale de `AJUSTES_POR_DEFECTO`, que ya trae el idioma
    // del móvil. Estaba copiado a mano con `IDIOMA_POR_DEFECTO` —o sea, el
    // castellano—, así que aunque lo de IndexedDB llegase bien había un
    // parpadeo en español antes de resolverse, y en `/alta` ese parpadeo era
    // la pantalla entera: se pinta y ya no cambia.
    const [ajustes, setAjustes] = useState<Ajustes>(AJUSTES_POR_DEFECTO);
    const [dia, setDia] = useState<DiaDeTrabajo | null>(null);
    const [cargandoDia, setCargandoDia] = useState(false);
    const [hayRed, setHayRed] = useState(navigator.onLine);
    const [resumenCola, setResumenCola] = useState<ResumenCola | null>(null);

    const pila = useRef<Pantalla[]>([]);
    const sesionRef = useRef<SesionGuardada | null>(null);
    sesionRef.current = sesion;

    const capacidades = useMemo(() => detectar(), []);

    // El cliente se construye UNA vez y lee el token en cada petición desde la
    // referencia: si se reconstruyera al renovar la sesión, la cola se quedaría
    // con el cliente viejo a mitad de una subida.
    const cliente = useMemo(
        () =>
            new Cliente({
                base: '/api',
                token: () => sesionRef.current?.jwt ?? null,
                alCaducar: () => {
                    // Vuelve al PIN sin tocar la cola ni el device_token: la
                    // credencial sigue siendo válida, lo que caducó es la
                    // sesión del turno.
                    setPantalla({ n: 'pin' });
                },
            }),
        []
    );

    const cola = useMemo(() => new Cola(cliente), [cliente]);

    const ir = useCallback((p: Pantalla, modo: Modo = 'apila') => {
        if (modo === 'raiz') {
            pila.current = [p];
            history.replaceState({ paso: 1 }, '');
        } else if (modo === 'reemplaza') {
            pila.current[Math.max(0, pila.current.length - 1)] = p;
            history.replaceState({ paso: pila.current.length }, '');
        } else {
            pila.current.push(p);
            history.pushState({ paso: pila.current.length }, '');
        }

        setSentido('adelante');
        setSello((n) => n + 1);
        setPantalla(p);
    }, []);

    const atras = useCallback(() => {
        history.back();
    }, []);

    // El botón atrás del sistema. Si no queda pila, no se sale de la app: se
    // vuelve a la lista del día. Salir a mitad de una limpieza por un gesto
    // involuntario sería perder el hilo de lo que se estaba grabando.
    useEffect(() => {
        const alVolver = (): void => {
            pila.current.pop();
            const anterior = pila.current[pila.current.length - 1];
            setSentido('atras');
            setSello((n) => n + 1);
            setPantalla(anterior ?? { n: 'hoy' });
        };

        addEventListener('popstate', alVolver);

        return () => removeEventListener('popstate', alVolver);
    }, []);

    useEffect(() => {
        const cambia = (): void => setHayRed(navigator.onLine);
        addEventListener('online', cambia);
        addEventListener('offline', cambia);

        return () => {
            removeEventListener('online', cambia);
            removeEventListener('offline', cambia);
        };
    }, []);

    useEffect(() => cola.escuchar(setResumenCola), [cola]);

    // --- Arranque -------------------------------------------------------

    useEffect(() => {
        void (async () => {
            const [s, a] = await Promise.all([leerSesion(), leerAjustes()]);
            setAjustes(a);

            // Que el navegador no tire la base local con vídeos sin subir.
            void pedirPersistencia();

            const invitacion = tokenDeInvitacion();
            const qr = tokenQrDeUrl();

            if (invitacion !== null) {
                limpiarUrl();
                pila.current = [{ n: 'alta', token: invitacion }];
                setPantalla({ n: 'alta', token: invitacion });

                return;
            }

            if (s === null) {
                pila.current = [{ n: 'alta', token: null }];
                setPantalla({ n: 'alta', token: null });

                return;
            }

            setSesion(s);
            const cache = await ultimoDia();
            if (cache !== null) {
                setDia(cache);
            }

            // Con la sesión del turno viva se entra directo; si caducó, al PIN.
            // El token de dispositivo sigue siendo válido en los dos casos.
            const viva = s.expira_at > Date.now() + 60_000;
            const primera: Pantalla = viva ? (qr !== null ? { n: 'qr' } : { n: 'hoy' }) : { n: 'pin' };
            if (qr !== null) {
                limpiarUrl();
            }
            pila.current = [primera];
            setPantalla(primera);

            if (viva) {
                void cola.arrancar('arranque de la app');
            }
        })();
    }, [cola]);

    // --- Sesión ---------------------------------------------------------

    const guardar = useCallback(
        async (s: SesionGuardada) => {
            await guardarSesion(s);
            setSesion(s);
            if (s.empleada.idioma !== ajustes.idioma) {
                // El idioma manda el servidor: es un dato de la empleada, no
                // una preferencia del móvil.
                const nuevos = { ...ajustes, idioma: s.empleada.idioma };
                setAjustes(nuevos);
                await guardarAjustes(nuevos);
            }
        },
        [ajustes]
    );

    const salir = useCallback(async () => {
        // Se borra la SESIÓN, no el dispositivo ni la cola: si quedan clips
        // por subir tienen que seguir ahí, y volver a entrar es un PIN.
        await borrarSesion();
        setSesion(null);
        pila.current = [{ n: 'pin' }];
        setPantalla({ n: 'pin' });
    }, []);

    const cambiarAjustes = useCallback(
        async (parcial: Partial<Ajustes>) => {
            // Tocar el conmutador del WiFi deja la marca de que lo ha elegido
            // ella. Sin esto, el valor inicial que pone oficina se volvería a
            // aplicar en el siguiente día de trabajo y le apagaría —o le
            // encendería— el conmutador sin que hubiera pasado nada.
            if (parcial.solo_wifi !== undefined) {
                parcial = { ...parcial, solo_wifi_elegido: true };
            }

            const nuevos = { ...ajustes, ...parcial };
            setAjustes(nuevos);
            await guardarAjustes(nuevos);
            if (parcial.solo_wifi === false) {
                void cola.arrancar('se ha desactivado solo-wifi');
            }
        },
        [ajustes, cola]
    );

    // --- El día ---------------------------------------------------------

    const recargarDia = useCallback(async () => {
        if (sesionRef.current === null) {
            return;
        }

        setCargandoDia(true);
        try {
            const datos = await cliente.dia();
            setDia(datos);
            await guardarDia(datos);
        } catch (e) {
            // Sin red se sigue con lo cacheado. No es un error que haya que
            // enseñar: es el caso normal en un portal de escaleras, y la
            // empleada tiene que poder trabajar igual.
            if (e instanceof ErrorApi && (e.codigo === 'sin_red' || e.reintentable)) {
                const cache = await ultimoDia();
                if (cache !== null) {
                    setDia(cache);
                }
            } else {
                throw e;
            }
        } finally {
            setCargandoDia(false);
        }
    }, [cliente]);

    // El día se recarga al entrar en 'hoy' y al volver a primer plano: entre
    // las 08:00 y las 14:00 oficina puede añadir un apartamento urgente.
    useEffect(() => {
        if (pantalla.n !== 'hoy' || sesion === null) {
            return;
        }
        void recargarDia();
    }, [pantalla.n, sesion, recargarDia]);

    useEffect(() => {
        const alVolver = (): void => {
            if (document.visibilityState === 'visible' && sesionRef.current !== null) {
                void recargarDia();
            }
        };
        document.addEventListener('visibilitychange', alVolver);

        return () => document.removeEventListener('visibilitychange', alVolver);
    }, [recargarDia]);

    // Los ajustes que decide la oficina, aplicados cuando llega el día.
    //
    // Hasta aquí la API no le mandaba al móvil NINGÚN ajuste, así que el
    // selector de calidad de vídeo del panel escribía en la base y no cambiaba
    // nada: la app grababa a 720p siempre. Y no es un mando cualquiera —bajar
    // a 480p es la medida 4 de las cinco del plan de subida en iPhone, 12 MB
    // por estancia en vez de 30, sobre una línea doméstica que es el cuello de
    // botella—.
    //
    // Va sobre `dia` y no dentro de `recargarDia()` a propósito: así se aplica
    // también con el día CACHEADO del arranque sin red, que es el caso en el
    // que más importa acertar la calidad —la empleada va a grabar seis clips
    // sin poder preguntar nada a nadie—.
    useEffect(() => {
        const politica = dia?.ajustes;
        if (politica === undefined) {
            return;
        }

        const cambios: Partial<Ajustes> = {};

        // La calidad la manda la oficina, como el idioma: en la app no hay
        // ningún control para cambiarla.
        if (politica.calidad_video !== ajustes.calidad) {
            cambios.calidad = politica.calidad_video;
        }

        // El WiFi solo mientras nadie lo haya tocado. El conmutador es de la
        // empleada —lo tiene en la pantalla de la cola— y pisárselo cada
        // mañana sería quitárselo.
        if (!ajustes.solo_wifi_elegido && politica.solo_wifi_por_defecto !== ajustes.solo_wifi) {
            cambios.solo_wifi = politica.solo_wifi_por_defecto;
        }

        if (Object.keys(cambios).length === 0) {
            return;
        }

        const nuevos = { ...ajustes, ...cambios };
        setAjustes(nuevos);
        void guardarAjustes(nuevos);
    }, [dia, ajustes]);

    // El idioma de la aplicación es el de los AJUSTES, no el de la ficha de la
    // empleada. La ficha lo decide al entrar —`guardar()` copia el idioma del
    // servidor a los ajustes en cada acceso—, pero a partir de ahí manda lo que
    // elija quien tiene el móvil en la mano. Leerlo de la sesión hacía que el
    // menú "Idioma" no sirviera para nada: se pulsaba English y no cambiaba.
    //
    // Sin `??`. Aquí había `ajustes.idioma ?? idiomaDelNavegador()` y era
    // código muerto —`Ajustes.idioma` no es opcional y `leerAjustes()` siempre
    // lo rellena—, así que parecía que la app respetaba el idioma del móvil
    // cuando no lo miraba nunca. Quien lo mira ahora es `AJUSTES_POR_DEFECTO`,
    // que es donde empieza todo. Un respaldo que no puede ejecutarse es peor
    // que no tenerlo: tapa la pregunta.
    const idioma: Idioma = ajustes.idioma;
    const t = useMemo(() => crearT(idioma), [idioma]);

    const valor: Contexto = {
        pantalla,
        sentido,
        sello,
        ir,
        atras,
        sesion,
        guardar,
        salir,
        ajustes,
        cambiarAjustes,
        idioma,
        t,
        cliente,
        cola,
        resumenCola,
        capacidades,
        dia,
        hayRed,
        cargandoDia,
        recargarDia,
    };

    return <ctx.Provider value={valor}>{children}</ctx.Provider>;
}
